import { supabase } from '../lib/supabase';

export const ticketService = {
  // Get user's tickets
  async getUserTickets(userId, options = {}) {
    const { status, eventId, upcoming = false } = options;

    let query = supabase?.from('tickets')?.select(`*,events(id, title, event_date, featured_image_url, venues(name, address_line_1, city)),ticket_types(name, price),order_items(orders(order_number, created_at))`)?.eq('user_id', userId);

    if (status) {
      query = query?.eq('status', status);
    }

    if (eventId) {
      query = query?.eq('event_id', eventId);
    }

    if (upcoming) {
      query = query?.gte('events.event_date', new Date()?.toISOString());
    }

    query = query?.order('created_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching user tickets:', error);
      throw error;
    }

    return data || [];
  },

  // Get ticket by ID
  async getTicketById(ticketId) {
    const { data, error } = await supabase?.from('tickets')?.select(`*,events(*,venues(*),event_categories(name, color)),ticket_types(*),order_items(orders(order_number, total_amount, created_at))`)?.eq('id', ticketId)?.single();

    if (error) {
      console.error('Error fetching ticket:', error);
      throw error;
    }

    return data;
  },

  // Get ticket by QR code (for scanning)
  async getTicketByQR(qrCode) {
    const { data, error } = await supabase?.from('tickets')?.select(`*,events(id, title, event_date, status),ticket_types(name),user_profiles!user_id(full_name, email)`)?.eq('qr_code', qrCode)?.single();

    if (error) {
      console.error('Error fetching ticket by QR:', error);
      throw error;
    }

    return data;
  },

  // Scan ticket (check-in)
  async scanTicket(ticketId, scannedBy, location = null, deviceInfo = null) {
    // First, get the ticket to validate
    const { data: ticket, error: ticketError } = await supabase?.from('tickets')?.select(`*,events(event_date, status)`)?.eq('id', ticketId)?.single();

    if (ticketError || !ticket) {
      throw new Error('Ticket not found');
    }

    // Validate ticket status
    let scanStatus = 'valid';
    let notes = '';

    if (ticket?.status === 'used') {
      scanStatus = 'already_used';
      notes = 'Ticket has already been used';
    } else if (ticket?.status === 'cancelled') {
      scanStatus = 'invalid';
      notes = 'Ticket has been cancelled';
    } else if (ticket?.events?.status !== 'active') {
      scanStatus = 'invalid';
      notes = 'Event is not currently active';
    } else if (new Date(ticket.events.event_date) < new Date()) {
      scanStatus = 'expired';
      notes = 'Event has already ended';
    }

    // Record the scan
    const { data: scan, error: scanError } = await supabase?.from('ticket_scans')?.insert({
        ticket_id: ticketId,
        scanned_by: scannedBy,
        scan_status: scanStatus,
        location: location,
        device_info: deviceInfo,
        notes: notes
      })?.select()?.single();

    if (scanError) {
      console.error('Error recording ticket scan:', scanError);
      throw scanError;
    }

    // If scan is valid, update ticket status and check-in time
    if (scanStatus === 'valid') {
      const { error: updateError } = await supabase?.from('tickets')?.update({
          status: 'used',
          check_in_time: new Date()?.toISOString(),
          checked_in_by: scannedBy
        })?.eq('id', ticketId);

      if (updateError) {
        console.error('Error updating ticket status:', updateError);
        throw updateError;
      }
    }

    return {
      scan,
      ticket: {
        ...ticket,
        status: scanStatus === 'valid' ? 'used' : ticket?.status
      },
      isValid: scanStatus === 'valid'
    };
  },

  // Transfer ticket to another user
  async initiateTransfer(ticketId, toEmail, fromUserId) {
    const transferToken = crypto.randomUUID();
    const expiresAt = new Date();
    expiresAt?.setDate(expiresAt?.getDate() + 7); // 7 days to accept

    const { data, error } = await supabase?.from('ticket_transfers')?.insert({
        ticket_id: ticketId,
        from_user_id: fromUserId,
        to_email: toEmail,
        transfer_token: transferToken,
        expires_at: expiresAt?.toISOString(),
        status: 'pending'
      })?.select()?.single();

    if (error) {
      console.error('Error initiating ticket transfer:', error);
      throw error;
    }

    return data;
  },

  // Accept ticket transfer
  async acceptTransfer(transferToken, toUserId) {
    // Get transfer details
    const { data: transfer, error: transferError } = await supabase?.from('ticket_transfers')?.select('*, tickets(*)')?.eq('transfer_token', transferToken)?.eq('status', 'pending')?.single();

    if (transferError || !transfer) {
      throw new Error('Transfer not found or has expired');
    }

    // Check if transfer is still valid
    if (new Date(transfer.expires_at) < new Date()) {
      throw new Error('Transfer has expired');
    }

    // Update ticket ownership
    const { error: ticketError } = await supabase?.from('tickets')?.update({
        user_id: toUserId,
        updated_at: new Date()?.toISOString()
      })?.eq('id', transfer?.ticket_id);

    if (ticketError) {
      console.error('Error updating ticket ownership:', ticketError);
      throw ticketError;
    }

    // Mark transfer as completed
    const { error: transferUpdateError } = await supabase?.from('ticket_transfers')?.update({
        to_user_id: toUserId,
        status: 'completed',
        completed_at: new Date()?.toISOString()
      })?.eq('id', transfer?.id);

    if (transferUpdateError) {
      console.error('Error updating transfer status:', transferUpdateError);
      throw transferUpdateError;
    }

    return true;
  },

  // Get ticket transfer history
  async getTransferHistory(userId) {
    const { data, error } = await supabase?.from('ticket_transfers')?.select(`
        *,
        tickets(ticket_number, events(title)),
        user_profiles!from_user_id(full_name),
        user_profiles!to_user_id(full_name)
      `)?.or(`from_user_id.eq.${userId},to_user_id.eq.${userId}`)?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching transfer history:', error);
      throw error;
    }

    return data || [];
  },

  // Get scan history for an event (for organizers)
  async getEventScanHistory(eventId, scannedBy = null) {
    let query = supabase?.from('ticket_scans')?.select(`
        *,
        tickets(ticket_number, holder_name, events(title)),
        user_profiles!scanned_by(full_name)
      `)?.eq('tickets.event_id', eventId);

    if (scannedBy) {
      query = query?.eq('scanned_by', scannedBy);
    }

    query = query?.order('scanned_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching scan history:', error);
      throw error;
    }

    return data || [];
  },

  // Get event check-in statistics
  async getEventCheckInStats(eventId) {
    const { data, error } = await supabase?.from('tickets')?.select('status, check_in_time')?.eq('event_id', eventId);

    if (error) {
      console.error('Error fetching check-in stats:', error);
      throw error;
    }

    const totalTickets = data?.length || 0;
    const checkedInTickets = data?.filter(t => t?.status === 'used')?.length || 0;
    const checkInRate = totalTickets > 0 ? (checkedInTickets / totalTickets) * 100 : 0;

    return {
      totalTickets,
      checkedInTickets,
      checkInRate,
      pendingTickets: totalTickets - checkedInTickets
    };
  }
};
function getTicketTypesByEventId(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: getTicketTypesByEventId is not implemented yet.', args);
  return null;
}

export { getTicketTypesByEventId };
function createTicketType(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: createTicketType is not implemented yet.', args);
  return null;
}

export { createTicketType };
function updateTicketType(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: updateTicketType is not implemented yet.', args);
  return null;
}

export { updateTicketType };
function deleteTicketType(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: deleteTicketType is not implemented yet.', args);
  return null;
}

export { deleteTicketType };